'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CALLRAIL_SWAP_EVENT, CallRailContextValue, CallRailProviderProps } from './types';

const CallRailContext = createContext<CallRailContextValue | null>(null);

declare global {
  interface Window {
    __callRailSwapReady?: boolean;
    CallTrkSwap?: {
      global?: {
        milestones?: Record<string, boolean>;
      };
    };
    CallTrk?: {
      swap?: (element?: Element | Document | HTMLElement) => void;
      knownSwapAssignments?: () => Record<string, unknown>;
    };
  }
}

function hasKnownSwapAssignments(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const assignments = window.CallTrk?.knownSwapAssignments?.();

    return Boolean(assignments && Object.keys(assignments).length > 0);
  } catch {
    return false;
  }
}

function isCallRailSwapComplete(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(window.__callRailSwapReady || hasKnownSwapAssignments());
}

export function CallRailProvider({ enabled, fallbackMs = 0, children }: CallRailProviderProps) {
  const [callRailReady, setCallRailReady] = useState(() => {
    if (!enabled) return true;

    return isCallRailSwapComplete();
  });

  useEffect(() => {
    if (!enabled || callRailReady) {
      return;
    }

    const markReady = () => {
      window.__callRailSwapReady = true;
      setCallRailReady(true);
    };

    const checkAndMarkReady = () => {
      if (isCallRailSwapComplete()) {
        markReady();
      }
    };

    checkAndMarkReady();

    window.addEventListener(CALLRAIL_SWAP_EVENT, checkAndMarkReady);

    const probeTimer = window.setInterval(checkAndMarkReady, 100);

    const fallbackTimer = fallbackMs > 0 ? window.setTimeout(markReady, fallbackMs) : undefined;

    return () => {
      window.removeEventListener(CALLRAIL_SWAP_EVENT, checkAndMarkReady);
      window.clearInterval(probeTimer);

      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
      }
    };
  }, [enabled, fallbackMs, callRailReady]);

  const value = useMemo(
    () => ({
      callRailReady,
      hasCallRailIntegration: enabled,
    }),
    [callRailReady, enabled],
  );

  return <CallRailContext.Provider value={value}>{children}</CallRailContext.Provider>;
}

export function useCallRailContext() {
  const context = useContext(CallRailContext);

  if (!context) {
    throw new Error('useCallRailContext must be used within a CallRailProvider');
  }

  return context;
}
